import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Trash2, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { getConversations, deleteConversation } from '../../api/aurora';
import { AppIcon } from '../../components/AppIcon';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
interface Conversation {
  id: string;
  title: string;
  updatedAt: string;
}
export function AuroraHistory() {
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetchHistory = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(false);
    try {
      const data = await getConversations(token);
      setConversations(data || []);
    } catch (err) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchHistory();
  }, [token]);
  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!token) return;
    try {
      await deleteConversation(token, id);
      setConversations(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      // console.error('Failed to delete conversation', err)
    }
  };
  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    const isYesterday = new Date(now.setDate(now.getDate() - 1)).toDateString() === date.toDateString();
    if (isYesterday) return 'Yesterday';
    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric'
    });
  };
  return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0 animate-fadeInUp" data-id="element-684">
      {/* Header */}
      <div className="flex items-center mb-6" data-id="element-685">
        <button onClick={() => navigate('/app/aurora')} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors mr-2" aria-label="Go back" data-id="element-686">
          <ArrowLeft size={24} className="text-text-primary" data-id="element-687" />
        </button>
        <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-688">
          Chat History
        </h1>
      </div>

      {/* Content */}
      {isLoading ? <div className="space-y-3" data-id="element-689">
          {[1, 2, 3, 4].map(i => <SkeletonBlock key={i} height="80px" borderRadius="16px" data-id="element-690" />)}
        </div> : error ? <div className="bg-danger/10 border border-danger/20 rounded-[16px] p-6 text-center" data-id="element-691">
          <MessageSquare size={32} className="text-danger mx-auto mb-3" data-id="element-692" />
          <h3 className="text-[16px] font-semibold text-text-primary mb-2" data-id="element-693">
            Failed to load history
          </h3>
          <Button onClick={fetchHistory} className="mx-auto mt-4" data-id="element-694">
            Try Again
          </Button>
        </div> : conversations.length === 0 ? <div className="bg-white rounded-[24px] p-10 shadow-card text-center flex flex-col items-center mt-8" data-id="element-695">
          <div className="w-[80px] h-[80px] bg-secondary rounded-full flex items-center justify-center mb-6" data-id="element-696">
            <MessageSquare size={32} className="text-primary" data-id="element-697" />
          </div>
          <h3 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-698">
            No conversations yet
          </h3>
          <p className="text-[15px] text-text-secondary mb-8 max-w-[280px]" data-id="element-699">
            Start a chat with Aurora to see your history here.
          </p>
          <Button onClick={() => navigate('/app/aurora')} data-id="element-700">
            Start a conversation
          </Button>
        </div> : <div className="space-y-3" data-id="element-701">
          <AnimatePresence data-id="element-702">
            {conversations.map(conv => <motion.div key={conv.id} layout initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95,
          height: 0,
          marginBottom: 0
        }} transition={{
          duration: 0.2
        }} className="group relative bg-white rounded-[16px] shadow-sm overflow-hidden" data-id="element-703">
                <div onClick={() => navigate(`/app/aurora/history/${conv.id}`)} className="p-4 flex items-center gap-4 cursor-pointer hover:bg-secondary/30 transition-colors z-10 relative bg-white" data-id="element-704">
                  <div className="flex-shrink-0" data-id="element-705">
                    <AppIcon size={40} data-id="element-706" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-707">
                    <h3 className="text-[15px] font-medium text-text-primary truncate" data-id="element-708">
                      {conv.title || 'New Conversation'}
                    </h3>
                    <p className="text-[13px] text-text-secondary mt-0.5" data-id="element-709">
                      {getRelativeTime(conv.updatedAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-710">
                    <button onClick={e => handleDelete(conv.id, e)} className="p-2 text-text-secondary hover:text-danger hover:bg-danger/10 rounded-full transition-colors opacity-0 group-hover:opacity-100 md:flex hidden" aria-label="Delete conversation" data-id="element-711">
                      <Trash2 size={18} data-id="element-712" />
                    </button>
                    <ChevronRight size={20} className="text-text-secondary" data-id="element-713" />
                  </div>
                </div>

                {/* Mobile swipe delete background */}
                <div className="absolute inset-y-0 right-0 w-[80px] bg-danger flex items-center justify-center z-0" data-id="element-714">
                  <Trash2 size={24} className="text-white" data-id="element-715" />
                </div>
              </motion.div>)}
          </AnimatePresence>
        </div>}
    </main>;
}